// src/pages/admin/page.tsx
//
// Das Admin-Panel für den Kunden.
// Aufrufbar unter: https://ihre-domain.de/admin
// Login via Supabase Auth (E-Mail + Passwort)

import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

// Gruppierung der Texte für eine übersichtliche Darstellung
const GROUPS = [
  {
    label: '🧭 Navigation & Menü',
    keys: ['navHome', 'navRooms', 'navLocation', 'navExcursions', 'navBreakfast', 'navContact', 'navBook'],
  },
  {
    label: '📋 Footer (unten auf jeder Seite)',
    keys: ['footerTagline', 'footerAboutText', 'footerAddress', 'footerPhone', 'footerEmail', 'footerHours', 'footerCopyright'],
  },
  {
    label: '🔔 Buchungs-Banner (unten beim Scrollen)',
    keys: ['stickyTitle', 'stickySubtitle', 'stickyButton'],
  },
  {
    label: '🛏️ Zimmer',
    keys: [
      'room1Name', 'room1Price', 'room1Desc',
      'room2Name', 'room2Price', 'room2Desc',
      'room3Name', 'room3Price', 'room3Desc',
      'room4Name', 'room4Price', 'room4Desc',
    ],
  },
  {
    label: '🏨 Hotel / Über uns',
    keys: ['hotelHeroTitle', 'hotelHeroText', 'hotelTradTitle', 'hotelTradText1', 'hotelTradText2',
           'hotelVal1Title', 'hotelVal1Text', 'hotelVal2Title', 'hotelVal2Text', 'hotelVal3Title', 'hotelVal3Text',
           'hotelCtaTitle', 'hotelCtaText', 'hotelCtaButton'],
  },
  {
    label: '📅 Buchungsseite',
    keys: ['bookHeroTitle', 'bookHeroText', 'bookAdviceTitle', 'bookAdviceText'],
  },
];

// Lesbare Labels für die Schlüssel
const KEY_LABELS: Record<string, string> = {
  navHome: 'Menüpunkt: Startseite',
  navRooms: 'Menüpunkt: Zimmer',
  navLocation: 'Menüpunkt: Lage',
  navExcursions: 'Menüpunkt: Ausflugsziele',
  navBreakfast: 'Menüpunkt: Frühstück',
  navContact: 'Menüpunkt: Kontakt',
  navBook: 'Button: Jetzt buchen',
  footerTagline: 'Footer-Slogan (Kursivtext)',
  footerAboutText: 'Footer: Über-uns-Text',
  footerAddress: 'Footer: Adresse',
  footerPhone: 'Footer: Telefonnummer',
  footerEmail: 'Footer: E-Mail',
  footerHours: 'Footer: Öffnungszeiten Rezeption',
  footerCopyright: 'Footer: Copyright-Zeile',
  stickyTitle: 'Buchungs-Banner: Titel',
  stickySubtitle: 'Buchungs-Banner: Untertitel',
  stickyButton: 'Buchungs-Banner: Button-Text',
  room1Name: 'Zimmer 1: Name',
  room1Price: 'Zimmer 1: Preis',
  room1Desc: 'Zimmer 1: Beschreibung',
  room2Name: 'Zimmer 2: Name',
  room2Price: 'Zimmer 2: Preis',
  room2Desc: 'Zimmer 2: Beschreibung',
  room3Name: 'Zimmer 3: Name',
  room3Price: 'Zimmer 3: Preis',
  room3Desc: 'Zimmer 3: Beschreibung',
  room4Name: 'Suite: Name',
  room4Price: 'Suite: Preis',
  room4Desc: 'Suite: Beschreibung',
  hotelHeroTitle: 'Hotel-Seite: Hauptüberschrift',
  hotelHeroText: 'Hotel-Seite: Einleitungstext',
  hotelTradTitle: 'Hotel-Seite: Abschnitt Tradition – Titel',
  hotelTradText1: 'Hotel-Seite: Tradition – Absatz 1',
  hotelTradText2: 'Hotel-Seite: Tradition – Absatz 2',
  hotelVal1Title: 'Werte: Herzlichkeit – Titel',
  hotelVal1Text: 'Werte: Herzlichkeit – Text',
  hotelVal2Title: 'Werte: Atmosphäre – Titel',
  hotelVal2Text: 'Werte: Atmosphäre – Text',
  hotelVal3Title: 'Werte: Lage – Titel',
  hotelVal3Text: 'Werte: Lage – Text',
  hotelCtaTitle: 'Hotel-Seite: CTA-Titel',
  hotelCtaText: 'Hotel-Seite: CTA-Text',
  hotelCtaButton: 'Hotel-Seite: CTA-Button',
  bookHeroTitle: 'Buchen: Hauptüberschrift',
  bookHeroText: 'Buchen: Einleitungstext',
  bookAdviceTitle: 'Buchen: Beratungs-Titel',
  bookAdviceText: 'Buchen: Beratungs-Text',
};

export default function AdminPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);
  const [authError, setAuthError] = useState('');
  const [content, setContent] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [loadingAuth, setLoadingAuth] = useState(true);

  // Prüfe ob bereits eingeloggt
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setLoggedIn(true);
        loadContent();
      }
      setLoadingAuth(false);
    });
  }, []);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setAuthError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setAuthError('Ungültige E-Mail oder falsches Passwort.');
    } else {
      setLoggedIn(true);
      loadContent();
    }
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    setLoggedIn(false);
    setContent({});
  }

  async function loadContent() {
    const { data } = await supabase.from('website_content').select('key, value');
    if (data) {
      const map: Record<string, string> = {};
      data.forEach(({ key, value }) => { map[key] = value; });
      setContent(map);
    }
  }

  async function saveField(key: string, value: string) {
    setSaving(s => ({ ...s, [key]: true }));
    await supabase
      .from('website_content')
      .upsert({ key, value }, { onConflict: 'key' });
    setSaving(s => ({ ...s, [key]: false }));
    setSaved(s => ({ ...s, [key]: true }));
    setTimeout(() => setSaved(s => ({ ...s, [key]: false })), 2000);
  }

  if (loadingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-stone-400 text-sm">Laden...</div>
      </div>
    );
  }

  // ── Login-Maske ──────────────────────────────────────────────────────────
  if (!loggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100 px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm">
          <div className="text-center mb-8">
            <img
              src="/images/brand/logo_admin.webp"
              width={512}
              height={341}
              loading="lazy"
              alt="Logo"
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-xl font-serif text-stone-800">Website-Verwaltung</h1>
            <p className="text-sm text-stone-500 mt-1">Hotel Alpenhof Gauting</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">E-Mail</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="ihre@email.de"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Passwort</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="••••••••"
                required
              />
            </div>
            {authError && (
              <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{authError}</p>
            )}
            <button
              type="submit"
              className="w-full bg-amber-700 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
            >
              Anmelden
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Admin-Panel ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-stone-50">

      {/* Header */}
      <div className="bg-amber-700 text-white px-6 py-4 flex items-center justify-between sticky top-0 z-10 shadow-md">
        <div>
          <h1 className="font-serif text-lg">Website-Verwaltung</h1>
          <p className="text-amber-200 text-xs">Hotel Alpenhof Gauting · Änderungen sind sofort live</p>
        </div>
        <div className="flex items-center gap-4">
          <a
            href="/"
            target="_blank"
            className="text-amber-200 hover:text-white text-xs flex items-center gap-1 transition-colors"
          >
            <i className="ri-external-link-line"></i>
            Website ansehen
          </a>
          <button
            onClick={handleLogout}
            className="bg-white/20 hover:bg-white/30 px-4 py-1.5 rounded-lg text-sm transition-colors"
          >
            Abmelden
          </button>
        </div>
      </div>

      {/* Hinweis */}
      <div className="max-w-3xl mx-auto px-4 pt-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex gap-3">
          <span className="text-amber-600 text-lg">💡</span>
          <div>
            <p className="text-sm font-medium text-amber-600">Wie funktioniert das?</p>
            <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
              Klicken Sie auf ein Feld, tippen Sie den neuen Text und klicken Sie auf <strong>Speichern</strong>.
              Die Änderung ist sofort auf der Website sichtbar – kein Neustart nötig.
            </p>
          </div>
        </div>
      </div>

      {/* Content-Gruppen */}
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-8 pb-20">
        {GROUPS.map((group) => (
          <div key={group.label} className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
            <div className="px-6 py-4 bg-stone-50 border-b border-stone-100">
              <h2 className="font-semibold text-stone-700 text-sm">{group.label}</h2>
            </div>
            <div className="divide-y divide-stone-50">
              {group.keys.map((key) => {
                const isLong = (content[key] ?? '').length > 80;
                return (
                  <div key={key} className="px-6 py-4">
                    <label className="block text-xs font-medium text-stone-500 mb-1.5">
                      {KEY_LABELS[key] ?? key}
                    </label>
                    <div className="flex gap-2 items-start">
                      {isLong ? (
                        <textarea
                          value={content[key] ?? ''}
                          onChange={e => setContent(c => ({ ...c, [key]: e.target.value }))}
                          rows={3}
                          className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-y"
                        />
                      ) : (
                        <input
                          type="text"
                          value={content[key] ?? ''}
                          onChange={e => setContent(c => ({ ...c, [key]: e.target.value }))}
                          className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        />
                      )}
                      <button
                        onClick={() => saveField(key, content[key] ?? '')}
                        disabled={saving[key]}
                        className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                          saved[key]
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-700 text-white hover:bg-amber-600'
                        }`}
                      >
                        {saving[key] ? '...' : saved[key] ? '✓ Gespeichert' : 'Speichern'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
